import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Plus, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, Megaphone } from "lucide-react";
import { toast } from "sonner";
import PrayerAnnouncement from "@/components/PrayerAnnouncement";
import MarkdownEditor from "@/components/MarkdownEditor";

const THEMES = [
  { value: "ivory", label: "Ivory & Gold (recommended for general use)" },
  { value: "gold", label: "Solid Gold (high emphasis — Eid / Ramadan)" },
  { value: "navy", label: "Navy & Gold (for navy contexts)" },
];

const PRESETS = [
  {
    name: "Eid Mubarak",
    title: "Eid Mubarak from FIC!",
    body: "**Eid prayer**: 8:30 AM at Dyewood Center.\nDoors open at 7:45 AM. Free breakfast after prayer, inshaAllah.",
    theme: "gold",
    cta_label: "View Schedule",
  },
  {
    name: "Ramadan",
    title: "Ramadan Schedule 2026",
    body: "**Iftar**: nightly at sunset (donations welcome).\n**Taraweeh**: starts ~30 min after Isha.\n**I'tikaaf**: last 10 nights — register at the office.",
    theme: "ivory",
    cta_label: "Full Schedule",
  },
  {
    name: "Friday Reminder",
    title: "Jumu'ah this week",
    body: "Khutbah at **2:00 PM**, Iqamah **2:30 PM**. Please arrive early — parking fills quickly.",
    theme: "ivory",
    cta_label: "",
  },
];

const BLANK = {
  enabled: true,
  title: "",
  body: "",
  theme: "ivory",
  cta_label: "",
  cta_url: "",
  order: 0,
};

export default function PrayerAnnouncementAdmin() {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null); // the currently-edited copy
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/prayer-announcements");
      setItems(r.data);
      if (r.data.length > 0) {
        setSelectedId((id) => id ?? r.data[0].id);
        setDraft((d) => d ?? { ...r.data[0] });
      } else {
        setSelectedId(null);
        setDraft(null);
      }
    } catch { toast.error("Failed to load announcements"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectItem = (it) => {
    setSelectedId(it.id);
    setDraft({ ...it });
  };

  const addNew = async () => {
    try {
      const nextOrder = items.length ? Math.max(...items.map((x) => x.order || 0)) + 1 : 0;
      const r = await api.post("/admin/prayer-announcements", {
        ...BLANK, title: "New announcement", order: nextOrder,
      });
      toast.success("New announcement created — edit & save");
      await load();
      setSelectedId(r.data.id);
      setDraft(r.data);
    } catch { toast.error("Could not create"); }
  };

  const save = async () => {
    if (!draft?.id) return;
    setSaving(true);
    try {
      await api.put(`/admin/prayer-announcements/${draft.id}`, {
        enabled: !!draft.enabled,
        title: draft.title || "",
        body: draft.body || "",
        theme: draft.theme || "ivory",
        cta_label: draft.cta_label || "",
        cta_url: draft.cta_url || "",
        order: draft.order ?? 0,
      });
      toast.success(draft.enabled ? "Saved — now LIVE" : "Saved (hidden)");
      await load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Delete this announcement permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/prayer-announcements/${id}`);
      toast.success("Deleted");
      if (selectedId === id) { setSelectedId(null); setDraft(null); }
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleVisible = async (it) => {
    try {
      await api.put(`/admin/prayer-announcements/${it.id}`, { ...it, enabled: !it.enabled });
      await load();
      toast.success(!it.enabled ? "Visible to public" : "Hidden");
    } catch { toast.error("Could not update"); }
  };

  const reorder = async (it, direction) => {
    const sorted = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
    const i = sorted.findIndex((x) => x.id === it.id);
    const j = i + direction;
    if (j < 0 || j >= sorted.length) return;
    const a = sorted[i], b = sorted[j];
    try {
      await Promise.all([
        api.put(`/admin/prayer-announcements/${a.id}`, { ...a, order: b.order || 0 }),
        api.put(`/admin/prayer-announcements/${b.id}`, { ...b, order: a.order || 0 }),
      ]);
      await load();
    } catch { toast.error("Could not reorder"); }
  };

  const applyPreset = (p) => {
    if (!draft) return;
    setDraft((d) => ({ ...d, title: p.title, body: p.body, theme: p.theme, cta_label: p.cta_label }));
    toast.message(`Loaded "${p.name}" preset — review and Save`);
  };

  if (loading) return <div className="text-ink/40">Loading…</div>;

  const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div data-testid="admin-announcement-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Prayer widget</div>
          <h1 className="font-serif text-4xl mt-2 text-ink">Announcement Slider</h1>
          <p className="mt-2 text-ink/60 text-sm max-w-2xl">
            Manage the rotating announcement cards shown on the Home and Prayer Times pages.
            Add multiple items — the slider auto-advances every ~6.5 s and pauses on hover / tap.
          </p>
        </div>
        <button
          onClick={addNew}
          data-testid="ann-new"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900"
        >
          <Plus size={14} /> New announcement
        </button>
      </div>

      <div className="grid lg:grid-cols-[280px,1fr,minmax(0,420px)] gap-6">
        {/* LIST */}
        <aside className="rounded-3xl bg-white border border-sand p-3 space-y-1 h-fit">
          {sortedItems.length === 0 && (
            <div className="p-5 text-center text-sm text-ink/55">
              <Megaphone size={20} className="mx-auto text-gold mb-2" />
              No announcements yet — click "New announcement".
            </div>
          )}
          {sortedItems.map((it, i) => (
            <div
              key={it.id}
              className={`rounded-xl p-3 text-sm cursor-pointer transition-colors ${
                selectedId === it.id ? "bg-gold/15 text-navy" : "hover:bg-sand/50 text-ink/80"
              }`}
              onClick={() => selectItem(it)}
              data-testid={`ann-list-${i}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{it.title || "(untitled)"}</div>
                  <div className="text-xs text-ink/50 truncate">{(it.body || "").replace(/[#*>_\-\n]/g, " ").trim().slice(0, 60) || "—"}</div>
                </div>
                <span className={`shrink-0 text-[9px] uppercase tracking-wider px-2 py-1 rounded-full ${
                  it.enabled ? "bg-navy text-cream" : "bg-sand text-ink/55"
                }`}>
                  {it.enabled ? "Live" : "Hidden"}
                </span>
              </div>
              <div className="flex items-center gap-1 mt-2 opacity-80">
                <IconBtn title="Up" onClick={(e) => { e.stopPropagation(); reorder(it, -1); }} disabled={i === 0} testId={`ann-up-${i}`}><ChevronUp size={12} /></IconBtn>
                <IconBtn title="Down" onClick={(e) => { e.stopPropagation(); reorder(it, 1); }} disabled={i === sortedItems.length - 1} testId={`ann-down-${i}`}><ChevronDown size={12} /></IconBtn>
                <IconBtn title={it.enabled ? "Hide" : "Show"} onClick={(e) => { e.stopPropagation(); toggleVisible(it); }} testId={`ann-toggle-${i}`}>
                  {it.enabled ? <EyeOff size={12} /> : <Eye size={12} />}
                </IconBtn>
                <IconBtn title="Delete" onClick={(e) => { e.stopPropagation(); removeItem(it.id); }} testId={`ann-delete-${i}`} danger>
                  <Trash2 size={12} />
                </IconBtn>
              </div>
            </div>
          ))}
        </aside>

        {/* EDITOR */}
        <div className="rounded-3xl bg-white border border-sand p-7 space-y-5 min-w-0">
          {!draft && (
            <div className="py-12 text-center text-ink/50">Select an announcement or create a new one to start editing.</div>
          )}
          {draft && (
            <>
              <label className="inline-flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!draft.enabled}
                  onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                  data-testid="ann-enabled"
                  className="w-5 h-5 accent-navy"
                />
                <span className="font-semibold text-ink">{draft.enabled ? "Visible to public" : "Hidden — turn on to publish"}</span>
              </label>

              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Quick presets</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {PRESETS.map((p) => (
                    <button
                      key={p.name}
                      type="button"
                      onClick={() => applyPreset(p)}
                      data-testid={`preset-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="rounded-full bg-gold/15 hover:bg-gold/25 text-navy px-4 py-1.5 text-xs font-semibold"
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>

              <Field label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} testId="ann-title" />

              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Body (rich text)</label>
                <div className="mt-2">
                  <MarkdownEditor
                    key={draft.id}
                    value={draft.body || ""}
                    onChange={(v) => setDraft({ ...draft, body: v })}
                    height={220}
                    testIdPrefix="ann-body"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Theme</label>
                <select
                  value={draft.theme || "ivory"}
                  onChange={(e) => setDraft({ ...draft, theme: e.target.value })}
                  data-testid="ann-theme"
                  className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy bg-white"
                >
                  {THEMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Button label (optional)" value={draft.cta_label || ""} onChange={(v) => setDraft({ ...draft, cta_label: v })} testId="ann-cta-label" />
                <Field label="Button URL (optional)" value={draft.cta_url || ""} onChange={(v) => setDraft({ ...draft, cta_url: v })} testId="ann-cta-url" placeholder="https://..." />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="ann-save"
                  className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-7 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900 disabled:opacity-60"
                >
                  {saving && <Loader2 size={14} className="animate-spin" />} Save announcement
                </button>
                <button
                  onClick={() => removeItem(draft.id)}
                  data-testid="ann-delete-current"
                  className="inline-flex items-center gap-2 rounded-full bg-red-50 text-red-700 border border-red-200 px-5 py-3 text-xs font-semibold uppercase tracking-wider hover:bg-red-100"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </>
          )}
        </div>

        {/* LIVE PREVIEW — all enabled items as they will appear on site */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-3">Live preview (all enabled)</div>
          <div className="rounded-3xl bg-navy p-5">
            <div className="rounded-2xl bg-cream/5 p-5">
              <PrayerAnnouncement
                items={[
                  // include unsaved draft if visible, else stored list
                  ...sortedItems.filter((it) => it.enabled && it.id !== draft?.id),
                  ...(draft && draft.enabled && (draft.title || draft.body) ? [draft] : []),
                ].sort((a, b) => (a.order || 0) - (b.order || 0))}
              />
              {sortedItems.filter((it) => it.enabled).length === 0 && !(draft && draft.enabled) && (
                <div className="text-cream/60 text-sm italic text-center py-8">
                  Enable at least one announcement to preview the slider.
                </div>
              )}
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mt-2">↑ this is how it appears above the prayer-time grid</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{label}</label>
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-2 w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy"
      />
    </div>
  );
}

function IconBtn({ children, onClick, title, disabled, testId, danger }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors ${
        danger ? "text-red-600 hover:bg-red-50" : "text-ink/60 hover:bg-sand/60 hover:text-navy"
      } disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}
